import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: saat kembali dari Google OAuth, biarkan AuthCallback menukar session_id lebih dulu.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const hasToken = !!localStorage.getItem("token");
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    // Verifikasi ke server dengan retry: pengguna Google hanya punya cookie httpOnly.
    // JANGAN logout saat error jaringan/timeout (penyebab "keluar sendiri" di HP & preview lambat);
    // hanya logout bila server benar-benar menolak auth (401/403).
    const verify = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await api.get("/auth/me", { timeout: 15000 });
          if (!cancelled) setUser(res.data);
          return;
        } catch (err) {
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem("token");
            if (!cancelled) setUser(null);
            return;
          }
          // error jaringan / timeout / 5xx -> coba lagi
          if (attempt < 2) await sleep(1000 * (attempt + 1));
        }
      }
      // Semua percobaan gagal (kemungkinan jaringan). Jangan hapus token supaya sesi tetap hidup.
      if (!cancelled && !hasToken) setUser(null);
    };

    verify().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const completeGoogleLogin = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("token");
    setUser(null);
  };

  const refresh = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, completeGoogleLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
